/**
 * CSS variable bridge. The Pixi renderer reads colours through this so a
 * theme switch (which only flips `body.theme-*`) is reflected on the next
 * redraw — no scene rebuild required.
 *
 * `readVar` parses `#rrggbb` and `#rgb` hex into a 0xRRGGBB number for Pixi.
 * Empty / unset / unparseable values fall back to a neutral grey so a
 * misnamed variable doesn't make a layer vanish.
 */

function parseHex(input: string): number {
  if (!input) return 0x808080
  const hex = input.startsWith('#') ? input.slice(1) : input
  if (hex.length === 3) {
    const r = parseInt(hex[0] + hex[0], 16)
    const g = parseInt(hex[1] + hex[1], 16)
    const b = parseInt(hex[2] + hex[2], 16)
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return 0x808080
    return (r << 16) | (g << 8) | b
  }
  if (hex.length === 6) {
    const n = parseInt(hex, 16)
    return Number.isNaN(n) ? 0x808080 : n
  }
  return 0x808080
}

/**
 * Read a CSS custom property from `<body>` and parse the hex into 0xRRGGBB.
 *
 * Why body and not :root: the theme overrides live on `body.theme-*` blocks.
 * Reading from `document.documentElement` (`<html>`) only picks up `:root`
 * declarations — html doesn't inherit from its descendant body, so the
 * theme overrides are invisible from there. Tailwind utilities and CSS
 * descendants of body see the overrides through normal inheritance, which
 * is why the panels retint correctly while the canvas (reading via this
 * function) didn't until this fix landed.
 *
 * Body always exists in the DOM by the time the renderer runs (it's
 * created in onMount, after Svelte has mounted the component tree under
 * `<div id="app">`).
 */
export function readVar(name: string): number {
  if (typeof document === 'undefined') return 0x808080
  const raw = getComputedStyle(document.body).getPropertyValue(name).trim()
  return parseHex(raw)
}
